import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText } from "lucide-react";

interface Product {
  id: string;
  name: string;
  cardType: string;
  cardNetwork: string;
  createdAt: string;
}

export default function ProductDrafted() {
  const { data: products, isLoading } = useQuery<Product[]>({
    queryKey: ["products", "drafted"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/products/drafted");
      if (!response.ok) throw new Error("Failed to fetch drafted products");
      return response.json();
    },
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Drafted Products</h1>
        <p className="text-muted-foreground">Products pending approval</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Draft Products</CardTitle>
          <CardDescription>{products?.length ?? 0} draft(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : products?.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No drafts</h3>
              <p className="text-muted-foreground">All products have been approved.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Network</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products?.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell><Badge variant="outline">{product.cardType}</Badge></TableCell>
                    <TableCell><Badge variant="secondary">{product.cardNetwork}</Badge></TableCell>
                    <TableCell>{new Date(product.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
